import type { EntityState } from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type { Except } from "type-fest";
import { z } from "zod";

import type { ProductId } from "../../products/product-id";
import { productIdZodSchema } from "../../products/product-id";

import {
    resourceIdZodSchema,
    type ResourceId,
} from "@@/shared/acquire/resources/resource-id";
import { now } from "@@/shared/date-time/date-time";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import type { TimeRange } from "@@/shared/date-time/time-range";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import { durationInMinutesZodSchema } from "@@/shared/date-time/time-spans";
import type { ProviderId } from "@@/shared/providers/provider-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import type { ExternalAccessId } from "./external-access-id";
import {
    externalAccessIdFactory,
    externalAccessIdZodSchema,
} from "./external-access-id";

type ExternalAccessPassCode = "PASSCODE";
type ExternalAccessParakey = "PARAKEY";

type ExternalAccessLocky = "LOCKY";

type ExternalAccessType =
    | ExternalAccessPassCode
    | ExternalAccessParakey
    | ExternalAccessLocky;

type BaseExternalAccess = {
    readonly _id: ExternalAccessId;
    readonly _type: "EXTERNAL_ACCESS";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _status: EntityState;
    readonly ownerId: ProviderId;

    productId?: ProductId; // Optional for backward compatibility

    readonly name: string;
    readonly validBefore: DurationInMinutes;
    readonly validAfter: DurationInMinutes;
    readonly valid?: TimeRange;

    readonly productIds: ProductId[];
    readonly resourceIds: ResourceId[];
};

type PassCodeAccess = BaseExternalAccess & {
    readonly type: ExternalAccessPassCode;
    readonly data: {
        code: string;
    };
};

type ParakeyAccess = BaseExternalAccess & {
    readonly type: ExternalAccessParakey;
    readonly data: {
        keyId: string;
        //integrationId: string;
    };
};

type LockyAccess = BaseExternalAccess & {
    readonly type: ExternalAccessLocky;
    readonly data: {
        roomId: string;
    };
};

type ProductExternalAccess = ParakeyAccess | PassCodeAccess | LockyAccess;

type FactoryParams = Except<
    ProductExternalAccess,
    "_type" | "_id" | "_createdAt" | "_status"
>;
const externalAccessFactory = (
    params: FactoryParams,
): ProductExternalAccess => {
    return {
        _id: externalAccessIdFactory(),
        _createdAt: now(),
        _type: "EXTERNAL_ACCESS",
        _status: "ACTIVE",
        ...params,
    };
};

const baseAccessFields = {
    _id: externalAccessIdZodSchema,
    _type: z.literal("EXTERNAL_ACCESS"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    ownerId: providerIdZodSchema,

    productId: productIdZodSchema.optional(), // Optional for backward compatibility

    name: z.string(),
    validBefore: durationInMinutesZodSchema,
    validAfter: durationInMinutesZodSchema,
    valid: timeRangeZodSchema.optional(),

    productIds: z.array(productIdZodSchema),
    resourceIds: z.array(resourceIdZodSchema),
};

const passCodeAccessZodSchema: z.ZodSchema<PassCodeAccess> = z.object({
    ...baseAccessFields,
    type: z.literal("PASSCODE"),
    data: z.object({ code: z.string() }),
});

const parakeyAccessZodSchema: z.ZodSchema<ParakeyAccess> = z.object({
    ...baseAccessFields,
    type: z.literal("PARAKEY"),
    data: z.object({
        keyId: z.string(),
        //  integrationId: providerIntegrationIdZodSchema,
    }),
});

const lockyAccessZodSchema: z.ZodSchema<LockyAccess> = z.object({
    ...baseAccessFields,
    type: z.literal("LOCKY"),
    data: z.object({ roomId: z.string() }),
});

const productExternalAccessZodSchema = z.union([
    parakeyAccessZodSchema,
    passCodeAccessZodSchema,
    lockyAccessZodSchema,
]);

export {
    externalAccessFactory,
    lockyAccessZodSchema,
    parakeyAccessZodSchema,
    passCodeAccessZodSchema,
    productExternalAccessZodSchema,
};
export type {
    ProductExternalAccess as ExternalAccess,
    ExternalAccessLocky,
    ExternalAccessParakey,
    ExternalAccessPassCode,
    ExternalAccessType,
    LockyAccess,
    ParakeyAccess,
    PassCodeAccess,
    ProductExternalAccess,
};
